<template>
  <div>
    <v-container>
      <h1>Remarket Release</h1>
      <p>
        Find remarketed submissions and release them to their agents. Each remarket is quoted once its data services are back, and
        only one that came back with a quote the agent can bind can be released; one whose quoting is stuck, or that didn't get a bindable
        quote, can be requoted. When the agent edits and requotes a released remarket, it becomes ordinary new business.
        Until a remarket is released, only admin and support users can see or open it. Policies that were cancelled or had lapsed
        when the remarket ran aren't remarketed; they're listed as skipped, with why. A remarket whose policy has been cancelled or
        has expired since isn't quoted or released; requote one whose policy has been reinstated.
      </p>

      <v-card class="mb-4">
        <v-card-title>Search</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="4">
              <v-text-field v-model="criteria.submissionId" label="Submission Id"
                            hint="All or part of the remarketed submission id or the one it was remarketed from" persistent-hint
                            outlined dense clearable data-testid="remarketReleaseSubmissionId" @keyup.enter="searchFromForm" />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="criteria.policy" label="Policy" hint="All or part of a policy number or policy id" persistent-hint
                            outlined dense clearable @keyup.enter="searchFromForm" />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="criteria.address" label="Address" hint="Parts of the street, city, state or postal code, e.g. TX or Tampa FL" persistent-hint
                            outlined dense clearable @keyup.enter="searchFromForm" />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="3">
              <v-text-field v-model="criteria.effectiveFrom" type="date" label="Effective from" outlined dense clearable />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field v-model="criteria.effectiveTo" type="date" label="Effective to" outlined dense clearable />
            </v-col>
            <v-col cols="12" md="3">
              <v-select v-model="criteria.status" :items="statusFilters" item-text="label" item-value="value" label="Status"
                        outlined dense />
            </v-col>
            <v-col cols="12" md="3">
              <FormButton label="Search" size="x-large" @click="searchFromForm" :disabled="loading"></FormButton>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-card class="mb-4">
        <v-card-title>
          Results ({{ rows.length }})
          <v-spacer />
          <FormButton :label="`Release (${releasable.length})`" size="large" class="mr-2"
                      :disabled="loading || releasable.length === 0 || releasable.length > maxReleases"
                      @click="confirmOpen = true" data-testid="remarketReleaseSelected"></FormButton>
          <FormButton :label="`Requote (${requotable.length})`" size="large" class="mr-2"
                      :disabled="loading || requotable.length === 0 || requotable.length > maxReleases"
                      @click="requote" data-testid="remarketRequoteSelected"></FormButton>
          <FormButton label="Download CSV" size="large" color="primary" :disabled="rows.length === 0" @click="downloadCsv"></FormButton>
        </v-card-title>
        <v-card-text>
          <v-alert v-if="truncated" type="warning" dense outlined>
            Showing the first {{ rows.length }} matches. Narrow the search to see the rest.
          </v-alert>
          <v-alert v-if="releasable.length > maxReleases || requotable.length > maxReleases" type="error" dense outlined>
            Release or requote up to {{ maxReleases }} at a time.
          </v-alert>
          <v-alert v-if="actionFailures.length" type="error" dense outlined data-testid="remarketReleaseFailures">
            <div>These weren't {{ failedAction }}:</div>
            <div v-for="failure in actionFailures" :key="failure.SubmissionId">{{ failure.SubmissionId }}: {{ failure.Message }}</div>
          </v-alert>

          <v-data-table v-model="selected"
                        :headers="headers"
                        :items="rows"
                        item-key="rowKey"
                        selectable-key="selectable"
                        show-select
                        :loading="loading"
                        :items-per-page="50"
                        :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
                        hide-default-footer
                        dense>
            <template v-slot:[`item.RemarketSubmissionId`]="{ item }">
              <router-link v-if="item.RemarketSubmissionId" :to="remarketedSubmissionLink(item)" target="_blank" rel="noopener">{{ item.RemarketSubmissionId }}</router-link>
            </template>
            <template v-slot:[`item.SourceAccountId`]="{ item }">
              <router-link :to="originalAccountLink(item)" target="_blank" rel="noopener">{{ item.SourceAccountId }}</router-link>
            </template>
            <template v-slot:[`item.SourcePolicyNumber`]="{ item }">
              <!-- the Amlink submission file, as the account page links its policy numbers -->
              <a v-if="item.SourcePolicyAmlinkUrl" :href="item.SourcePolicyAmlinkUrl" target="_blank" rel="noopener">{{ item.SourcePolicyNumber }}</a>
              <span v-else>{{ item.SourcePolicyNumber }}</span>
            </template>
            <template v-slot:[`item.SourceTermTotal`]="{ item }">
              {{ sourceTermLabel(item) }}
            </template>
            <template v-slot:[`item.EffectiveDate`]="{ item }">
              {{ formatDateForDisplay(item.EffectiveDate) }}
            </template>
            <template v-slot:[`item.AllowedSalesChannels`]="{ item }">
              {{ item.AllowedSalesChannels.join(', ') }}
            </template>
            <template v-slot:[`item.RemarketedAt`]="{ item }">
              {{ formatDateTimeForDisplay(item.RemarketedAt) }}
            </template>
            <template v-slot:[`item.ChannelQuotes`]="{ item }">
              <!-- one line per channel the remarket was quoted on; hover one that didn't quote to see why -->
              <div v-for="quote in item.ChannelQuotes" :key="quote.SalesChannel" class="text-no-wrap">
                <span :class="{ 'font-weight-bold': quote.Status === 'Ok' && quote.Total != null, 'error--text': quote.Status === 'Error' }">
                  {{ channelQuoteLabel(quote) }}
                </span>
                <v-tooltip v-if="channelQuoteDetails(quote).length" bottom max-width="480">
                  <!-- a button so the details can be reached from the keyboard too -->
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon x-small class="ml-1" v-bind="attrs" v-on="on" :aria-label="channelQuoteDetailsLabel(quote)">
                      <v-icon small>mdi-information-outline</v-icon>
                    </v-btn>
                  </template>
                  <div v-for="line in channelQuoteDetails(quote)" :key="line">{{ line }}</div>
                </v-tooltip>
              </div>
            </template>
            <template v-slot:[`item.Status`]="{ item }">
              {{ releaseStatusLabel(item) }}
              <div v-if="item.ReleasedAt" class="caption">{{ formatDateTimeForDisplay(item.ReleasedAt) }} by {{ item.ReleasedBy }}</div>
              <div v-if="item.VoidedAt" class="caption">Requoted {{ formatDateTimeForDisplay(item.VoidedAt) }} by {{ item.VoidedBy }}</div>
            </template>
            <!-- custom footer so the rows-per-page menu opens upward instead of off the bottom of the page, as ResultsTable does -->
            <template v-slot:footer="{ props, on }">
              <div class="v-data-footer">
                <div class="v-data-footer__select">
                  Rows per page:
                  <v-select :value="props.options.itemsPerPage"
                            :items="rowsPerPageItems(props.itemsPerPageOptions)"
                            :menu-props="{ top: true, offsetY: true }"
                            min-width="75px"
                            hide-details
                            @change="updateTableOptions(on, props.options, { itemsPerPage: $event, page: 1 })" />
                </div>
                <div class="v-data-footer__pagination">
                  {{ footerRange(props.pagination) }}
                </div>
                <div class="v-data-footer__icons-before">
                  <v-btn icon :disabled="props.pagination.page <= 1" aria-label="Previous page"
                         @click="updateTableOptions(on, props.options, { page: props.pagination.page - 1 })">
                    <v-icon>mdi-chevron-left</v-icon>
                  </v-btn>
                </div>
                <div class="v-data-footer__icons-after">
                  <v-btn icon :disabled="props.pagination.page >= props.pagination.pageCount" aria-label="Next page"
                         @click="updateTableOptions(on, props.options, { page: props.pagination.page + 1 })">
                    <v-icon>mdi-chevron-right</v-icon>
                  </v-btn>
                </div>
              </div>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>

      <v-dialog v-model="confirmOpen" max-width="520">
        <v-card>
          <v-card-title>Release {{ releasable.length }} remarketed submission{{ releasable.length === 1 ? '' : 's' }}?</v-card-title>
          <v-card-text>
            Their agents will see them on their dashboard and in search, and can open them. This can't be undone from this page.
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="confirmOpen = false">Cancel</v-btn>
            <v-btn color="primary" :disabled="loading" @click="release" data-testid="remarketReleaseConfirm">Release</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
  import FormButton from '@/components/form/FormButton.vue';
  import { formatDateForDisplay, formatDateTimeForDisplay } from '@/scripts/helper';
  import {
    MAX_RELEASES_PER_REQUEST, RELEASE_STATUS_FILTERS, canRelease, canRequote, channelQuoteDetails, channelQuoteDetailsLabel, channelQuoteLabel, originalAccountLink, releaseStatusLabel, remarketedSubmissionLink, rowKey, sourceTermLabel, toCsv
  } from '@/scripts/remarketRelease';
  import { RemarketReleaseService } from '@/api/RemarketReleaseService';

  export default {
    name: 'RemarketRelease',
    components: {
      FormButton
    },
    data () {
      return {
        loading: false,
        confirmOpen: false,
        maxReleases: MAX_RELEASES_PER_REQUEST,
        statusFilters: RELEASE_STATUS_FILTERS,
        criteria: {
          submissionId: null,
          policy: null,
          address: null,
          effectiveFrom: null,
          effectiveTo: null,
          status: 'Unreleased'
        },
        rows: [],
        truncated: false,
        // the criteria of the last search actually run, so a refresh after a release doesn't pick up edits not yet searched
        searchedCriteria: null,
        searchSequence: 0,
        selected: [],
        actionFailures: [],
        failedAction: '',
        headers: [
          { text: 'Remarketed', value: 'RemarketSubmissionId' },
          { text: 'Original Account', value: 'SourceAccountId' },
          { text: 'Policy Number', value: 'SourcePolicyNumber' },
          { text: 'Current Term', value: 'SourceTermTotal' },
          { text: 'Insured', value: 'InsuredName' },
          { text: 'Risk Address', value: 'RiskAddress' },
          { text: 'Effective', value: 'EffectiveDate' },
          { text: 'Agent', value: 'AgentName' },
          { text: 'Agent Email', value: 'AgentEmail' },
          { text: 'Sales Channels', value: 'AllowedSalesChannels', sortable: false },
          { text: 'Remarketed / Skipped On', value: 'RemarketedAt' },
          { text: 'Quotes', value: 'ChannelQuotes', sortable: false },
          { text: 'Status', value: 'Status' }
        ]
      };
    },
    computed: {
      // a selection can mix rows to release and rows to requote; each action only sends the rows it applies to
      releasable () {
        return this.selected.filter(canRelease);
      },
      requotable () {
        return this.selected.filter(canRequote);
      }
    },
    created () {
      this.searchFromForm();
    },
    methods: {
      formatDateForDisplay,
      formatDateTimeForDisplay,
      releaseStatusLabel,
      channelQuoteLabel,
      channelQuoteDetails,
      channelQuoteDetailsLabel,
      remarketedSubmissionLink,
      originalAccountLink,
      sourceTermLabel,
      rowsPerPageItems (options) {
        return (options || []).map(o => ({ text: o === -1 ? 'All' : String(o), value: o }));
      },
      updateTableOptions (on, options, changes) {
        on['update:options']({ ...options, ...changes });
      },
      footerRange ({ pageStart, pageStop, itemsLength }) {
        return `${itemsLength ? pageStart + 1 : 0}-${pageStop} of ${itemsLength}`;
      },
      searchFromForm () {
        this.search({ ...this.criteria });
      },
      refresh () {
        this.search(this.searchedCriteria ?? { ...this.criteria });
      },
      // the axios plugin shows the error toast for a failed request; the catches here only keep the page usable
      search (criteria) {
        // only the newest search's results are shown, however the responses arrive
        const sequence = ++this.searchSequence;
        this.searchedCriteria = criteria;
        this.loading = true;
        RemarketReleaseService.search(criteria)
          .then((result) => {
            if (sequence !== this.searchSequence) return;
            this.rows = result.Rows.map(row => ({ ...row, rowKey: rowKey(row), selectable: canRelease(row) || canRequote(row) }));
            this.truncated = result.Truncated;
            this.selected = [];
          })
          .catch(() => {})
          .finally(() => {
            if (sequence === this.searchSequence) this.loading = false;
          });
      },
      release () {
        this.loading = true;
        const submissionIds = this.releasable.map(row => row.RemarketSubmissionId);
        RemarketReleaseService.release({ body: { submissionIds } })
          .then((results) => {
            const released = results.filter(r => r.IsReleased).length;
            this.actionFailures = results.filter(r => !r.IsReleased);
            this.failedAction = 'released';
            this.$store.dispatch('toastSuccess', { Message: `Released ${released} of ${results.length}` });
          })
          .catch(() => {})
          .finally(() => {
            this.confirmOpen = false;
            this.loading = false;
            this.refresh();
          });
      },
      requote () {
        this.loading = true;
        const submissionIds = this.requotable.map(row => row.RemarketSubmissionId);
        RemarketReleaseService.requote({ body: { submissionIds } })
          .then((results) => {
            const requoted = results.filter(r => r.IsRequoted).length;
            this.actionFailures = results.filter(r => !r.IsRequoted);
            this.failedAction = 'requoted';
            this.$store.dispatch('toastSuccess', { Message: `Sent ${requoted} of ${results.length} for quoting again` });
          })
          .catch(() => {})
          .finally(() => {
            this.loading = false;
            this.refresh();
          });
      },
      downloadCsv () {
        // the byte order mark tells Excel it's UTF-8, so accented names and addresses open correctly
        const blob = new Blob(['\uFEFF', toCsv(this.rows)], { type: 'text/csv;charset=utf-8' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `remarket-release-${new Date().toISOString().substring(0, 10)}.csv`;
        // attached for the click, and the URL kept a moment, so every browser starts the download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(link.href), 1000);
      }
    }
  };
</script>
